import { useEffect, useRef, useState } from "react"
import styled from "styled-components"
import Modal from "~app/Modal"
import useInvalidationReasons from "~hooks/useInvalidationReasons"
import { NoTableData } from "~styled/table"
import CopyCell from "~components/CopyCell"
import colors from "~constants/colors"

interface Props {
  userEmail: string
  onChangeSelectedItem: (v: string) => void
  onCloseModal: () => void
  onSubmit: () => void
  isLoading: boolean
  isOpen: boolean
  // Reason recommended for this document (e.g. "OLD_DOCUMENTATION"). When it's a
  // valid option, the modal opens with it pre-selected to speed up the operator.
  suggestedReason?: string
}

const InvalidatePooModal = ({
  userEmail,
  onChangeSelectedItem,
  onCloseModal,
  onSubmit,
  isLoading,
  isOpen,
  suggestedReason,
}: Props) => {
  const {
    refetch: fetchInvalidationReasons,
    data: pooInvalidationReasons,
    isError: isErrorFetchingInvalidationReasons,
    isFetching: isFetchingReasons,
  } = useInvalidationReasons()
  const [selected, setSelected] = useState<string>("")
  // Guards the one-time pre-selection so it doesn't override a manual change.
  const prefilledRef = useRef<boolean>(false)

  const reasons = pooInvalidationReasons ?? []

  useEffect(() => {
    ;(async () => {
      await fetchInvalidationReasons()
    })()
  }, [])

  // On open, pre-select the suggested reason once it's available (and a valid
  // option). On close, clear so the modal opens fresh next time.
  useEffect(() => {
    if (!isOpen) {
      setSelected("")
      prefilledRef.current = false
      return
    }
    if (
      !prefilledRef.current &&
      suggestedReason &&
      reasons.includes(suggestedReason)
    ) {
      setSelected(suggestedReason)
      onChangeSelectedItem(suggestedReason)
      prefilledRef.current = true
    }
  }, [isOpen, suggestedReason, reasons])

  const handleSelect = (reason: string) => {
    setSelected(reason)
    onChangeSelectedItem(reason)
  }

  const close = () => {
    setSelected("")
    onChangeSelectedItem("")
    onCloseModal()
  }

  const submit = () => {
    if (!selected || isLoading) return
    onSubmit()
  }

  // Keyboard: 1–9 pick the matching reason, Enter rejects (when a reason is
  // selected). Esc-to-cancel is handled by react-modal's onRequestClose.
  useEffect(() => {
    if (!isOpen) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key >= "1" && e.key <= "9") {
        const idx = Number(e.key) - 1
        if (reasons[idx]) {
          e.preventDefault()
          handleSelect(reasons[idx])
        }
      } else if (e.key === "Enter") {
        if (selected && !isLoading) {
          e.preventDefault()
          submit()
        }
      }
    }
    window.addEventListener("keydown", onKey)
    return () => window.removeEventListener("keydown", onKey)
  }, [isOpen, reasons, selected, isLoading])

  return (
    <Modal
      isButtonLoading={isLoading || isErrorFetchingInvalidationReasons}
      isButtonDisabled={!selected}
      cancelButton={true}
      maxWidth={"520px"}
      isOpen={isOpen}
      buttonLabel={"Reject"}
      onRequestCancel={close}
      onRequestClose={close}
      onRequestConfirm={submit}
    >
      <Header>
        <Title>Reject proof of ownership</Title>
        <EmailRow>
          <span>from</span>
          <CopyCell
            textColor={colors.black}
            text={userEmail}
            rowData={userEmail}
          />
        </EmailRow>
      </Header>

      {isErrorFetchingInvalidationReasons && (
        <NoTableData>Error loading</NoTableData>
      )}

      {isFetchingReasons ? (
        <NoTableData>Loading...</NoTableData>
      ) : (
        <>
          <SectionLabel>Select a reason</SectionLabel>
          <ReasonList>
            {reasons.map((reason, i) => {
              const isSelected = selected === reason
              return (
                <ReasonRow
                  key={reason}
                  type="button"
                  $selected={isSelected}
                  onClick={() => handleSelect(reason)}
                >
                  <NumBadge $selected={isSelected}>
                    {i < 9 ? i + 1 : "•"}
                  </NumBadge>
                  <span>{reason}</span>
                </ReasonRow>
              )
            })}
          </ReasonList>
          <Hint>
            Press <Kbd>1</Kbd>–<Kbd>{Math.min(reasons.length, 9)}</Kbd> to pick
            · <Kbd>Enter</Kbd> to reject · <Kbd>Esc</Kbd> to cancel
          </Hint>
        </>
      )}
    </Modal>
  )
}

const Header = styled.div`
  margin-bottom: 18px;
`

const Title = styled.h3`
  margin: 0 0 4px;
  font-size: 16px;
  font-weight: 600;
  color: ${colors.blue1};
`

const EmailRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: ${colors.gray6};
`

const SectionLabel = styled.span`
  display: block;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  color: ${colors.gray6};
  margin-bottom: 8px;
`

const ReasonList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  max-height: 320px;
  overflow-y: auto;
`

const ReasonRow = styled.button<{ $selected: boolean }>`
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 11px 14px;
  font-size: 14px;
  text-align: left;
  color: ${colors.blue3};
  background: ${p => (p.$selected ? "rgba(245, 75, 94, 0.08)" : colors.white)};
  border: 1px solid ${p => (p.$selected ? colors.red : colors.gray9)};
  border-radius: 8px;
  cursor: pointer;
  transition:
    background 0.15s,
    border-color 0.15s;

  &:hover {
    border-color: ${p => (p.$selected ? colors.red : colors.gray6)};
    background: ${p =>
      p.$selected ? "rgba(245, 75, 94, 0.12)" : colors.gray5};
  }
`

const NumBadge = styled.span<{ $selected: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 20px;
  height: 20px;
  font-size: 11px;
  font-weight: 700;
  border-radius: 5px;
  color: ${p => (p.$selected ? colors.white : colors.gray6)};
  background: ${p => (p.$selected ? colors.red : colors.gray2)};
`

const Hint = styled.div`
  margin: 18px 0 12px;
  font-size: 12px;
  color: ${colors.gray6};
`

const Kbd = styled.kbd`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 16px;
  height: 16px;
  padding: 0 4px;
  font-family: inherit;
  font-size: 10px;
  font-weight: 600;
  line-height: 1;
  color: ${colors.gray6};
  background: ${colors.gray1};
  border: 1px solid ${colors.gray2};
  border-radius: 3px;
`

export default InvalidatePooModal
